import { PhotoLibrary as PhotoLibraryIcon } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useMediaTags } from "../admin/dictionaries/useMediaTags";
import PrettyImagesPreview from "../components/imagesPreview/PrettyImagesPreview";
import type { MediaImage } from "../media/mediaApi";
import { useFilteredAndOrderedImages } from "../media/useFilteredAndOrderedImages";
import { useMediaSearch } from "../media/useMediaSearch";
import { useDownloadImages } from "../misc/useDownloadImages";
import MediaLightbox from "./MediaLightbox";

const MediaBank = () => {
  const { searchCriteria, setSearchCriteria } = useMediaSearch();
  const { tags } = useMediaTags();
  const selectedTagNames = tags.filter((tag) => searchCriteria.tags.includes(tag.id)).map((tag) => tag.name);
  const { images, isLoading } = useFilteredAndOrderedImages(searchCriteria.text, selectedTagNames);
  const [selectedImages, setSelectedImages] = useState<MediaImage[]>([]);
  const [lightboxImage, setLightboxImage] = useState<MediaImage | null>(null);
  const selectionMode = selectedImages.length > 0;

  const { download } = useDownloadImages();

  const handleDownload = async (imageSize: "original" | "www" | "thumbnail") => {
    if (!selectedImages) {
      return;
    }
    await download(
      selectedImages.map((i) => ({ name: i.name, url: i[imageSize].url, source: i.source, info: i.info })),
      { zipFileName: `images_${imageSize}.zip` },
    );
    setSelectedImages([]);
  };

  const handleDownloadSingleImage = async (imageSize: "original" | "www" | "thumbnail", image: MediaImage) => {
    await download(
      [image].map((i) => ({ name: i.name, url: i[imageSize].url, source: i.source, info: i.info })),
      { zipFileName: `images_${imageSize}.zip` },
    );
  };

  const handleToggleImageSelection = (image: MediaImage) => {
    if (selectedImages?.find((i) => i.id === image.id)) {
      setSelectedImages((images) => (images ?? []).filter((i) => i.id !== image.id));
    } else {
      setSelectedImages((images) => [...(images ?? []), image]);
    }
  };

  return (
    <Box
      sx={{
        animation: "fadeIn 0.3s ease-in-out",
        width: "100%",
        px: { xs: 2, sm: 3, md: 4 },
        pb: 4,
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ mb: 3, alignItems: "center" }}>
        <PhotoLibraryIcon color="primary" />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Media bank
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zdjęcia demo z wycieczek — wyszukuj, zaznaczaj i pobieraj materiały promocyjne.
          </Typography>
        </Box>
      </Stack>

      <PrettyImagesPreview
        isLoading={isLoading}
        images={images}
        selectedImages={selectedImages}
        selectionMode={selectionMode}
        tags={tags}
        search={searchCriteria}
        onSearchChange={(text, tags) => setSearchCriteria({ text, tags })}
        onToggleImageSelection={handleToggleImageSelection}
        onSelectAllImages={() => setSelectedImages(images)}
        onClearSelection={() => setSelectedImages([])}
        onDownloadImages={handleDownload}
        onImageClick={setLightboxImage}
      />
      <MediaLightbox
        image={lightboxImage}
        images={images}
        onClose={() => setLightboxImage(null)}
        onDownload={handleDownloadSingleImage}
        onNavigate={setLightboxImage}
      />
    </Box>
  );
};

export default MediaBank;
